<template>
  <PageHeader
    breadcrumb
    :items="[
      { label: $t('ui.entities.setting', 2), to: '/settings' },
      { label: $t('ui.entities.manufacturer', 2), to: '/settings/manufacturers' }
    ]"
  ></PageHeader>

  <Card>
    <DataTable
      ref="dt"
      v-model:filters="filters"
      export-filename="Manufacturers"
      :value="manufacturers"
      :loading="isLoading"
      removable-sort
      sort-field="id"
      :sort-order="-1"
      :rows="20"
      data-key="id"
      column-resize-mode="fit"
      :paginator="true"
      responsive-layout="scroll"
      filter-display="menu"
      :rows-per-page-options="[10, 20, 50]"
      state-key="dt-settings-manufacturers"
      state-storage="session"
      current-page-report-template="Showing {startRecord}-{endRecord} out of {totalRecords}"
      :global-filter-fields="['id', 'name']"
    >
      <template #empty>No records found</template>
      <template #header>
        <div class="flex justify-between">
          <span class="flex justify-between gap-4">
            <span>
              <Input
                v-model="filters['global'].value"
                :placeholder="$t('ui.common.search', 1)"
                icon="search"
              />
            </span>
            <Button
              :label="$t('ui.actions.clear', 1)"
              severity="secondary"
              type="button"
              outlined
              icon="filter-slash"
              @click="clearFilters()"
            />
          </span>
        </div>
      </template>

      <Column
        field="id"
        header="ID"
        :sortable="true"
      >
        <template #body="{ data }">
          <div>{{ data.id }}</div>
        </template>
      </Column>

      <Column
        field="name"
        header="Name"
        :sortable="true"
      >
        <template #body="{ data }">
          <div>{{ data.name }}</div>
        </template>
      </Column>

      <Column
        field="squareLogoUrl"
        header="Square logo"
      >
        <template #body="{ data }">
          <Image
            :src="data.squareLogoUrl"
            image-class="max-w-8"
          />
        </template>
      </Column>

      <Column
        field="logoUrl"
        header="Logo"
      >
        <template #body="{ data }">
          <Image
            :src="data.logoUrl"
            image-class="h-8 max-w-16"
          />
        </template>
      </Column>

      <Column
        field="accentColor"
        header="Brand Colour"
      >
        <template #body="{ data }">
          <div class="flex items-center gap-2">
            <Chip
              v-if="data.accentColor"
              :color="data.accentColor"
            >
              {{ _toUpper(data.accentColor) }}
            </Chip>
          </div>
        </template>
      </Column>

      <Column
        field="branding"
        header="Brand Profile"
      >
        <template #body="{ data }">
          <div>{{ data.branding?.name }}</div>
        </template>
      </Column>

      <Column
        field="regions"
        header="Regions"
      >
        <template #body="{ data }">
          <div class="flex gap-2">
            <Chip
              v-for="(region, index) in data.regions"
              :key="index"
            >
              <img
                :src="regionsList.find(r => r.value == region.toLowerCase()).image"
                class="w-5 mr-2"
              />
              {{ region.toUpperCase() }}
            </Chip>
          </div>
        </template>
      </Column>

      <Column
        field="isActive"
        header="Active"
        :sortable="true"
        :show-filter-match-modes="false"
        :show-filter-operator="false"
      >
        <template #body="{ data }">
          <Chip
            v-if="data.isActive"
            :rounded="false"
            outlined
            size="sm"
            dot
            severity="success"
          >
            Active
          </Chip>
          <Chip
            v-else
            :rounded="false"
            outlined
            size="sm"
            dot
            severity="danger"
          >
            Disabled
          </Chip>
        </template>
        <template #filter="{ filterModel }">
          <Select
            :teleport="false"
            v-model="filterModel.value"
            :options="[
              { value: 1, label: 'Active' },
              { value: 0, label: 'Disabled' }
            ]"
            mode="multiple"
            :chips="false"
            value-key="value"
            label-key="label"
            placeholder="All"
          >
            <template #option="slotProps">
              {{ slotProps.option.label }}
            </template>
          </Select>
        </template>
      </Column>

      <Column :sortable="false">
        <template #body="{ data }">
          <div>
            <Button
              v-if="data.brandProfileId"
              v-tippy="'Edit Brand Profile'"
              icon="palette"
              plain
              severity="secondary"
              @click="editBrandProfile(data)"
            ></Button>
          </div>
        </template>
      </Column>
    </DataTable>
  </Card>

  <teleport to="#modals-container">
    <Modal
      :is-open="brandProfileModalIsOpen"
      title="Edit Brand Profile"
      icon="palette"
      :buttons="['ok', 'close']"
      close-text="Cancel"
      ok-text="Save"
      :show-loader-on-confirm="true"
      :is-confirming="brandProfileApiRequest.isLoading.value"
      :disable-close="brandProfileApiRequest.isLoading.value"
      min-width="600"
      overflow-behaviour="visible"
      @close-modal="brandProfileModalIsOpen = false"
      @ok-modal="submitBrandProfile()"
    >
      <div class="grid grid-cols-2 gap-x-6">
        <FormGroup
          :label="$t('ui.common.name')"
          :is-required="true"
        >
          <Input
            v-model="brandProfileState.name"
            :has-error="v$profile.name.$error"
          />
          <InputError :has-error="v$profile.name.$error">{{ v$profile.name.$errors[0]?.$message }}</InputError>
        </FormGroup>
        <FormGroup label="Accent Colour">
          <div class="flex items-center gap-2">
            <Input
              v-model="brandProfileState.accentColor"
              placeholder="#000000"
            />
            <span
              v-if="brandProfileState.accentColor"
              class="inline-block h-6 w-6 shrink-0 rounded-full border border-[var(--border-color)]"
              :style="{ backgroundColor: brandProfileState.accentColor }"
            ></span>
          </div>
        </FormGroup>
        <FormGroup label="UI Gradient 1">
          <Input
            v-model="brandProfileState.uiGradient1"
            placeholder="#000000"
          />
        </FormGroup>
        <FormGroup label="UI Gradient 2">
          <Input
            v-model="brandProfileState.uiGradient2"
            placeholder="#000000"
          />
        </FormGroup>
      </div>

      <div class="mt-2">
        <div class="font-medium mb-2">Logos</div>
        <div class="grid grid-cols-2 gap-x-6">
          <div class="border border-[var(--border-color)] rounded-md p-3">
            <div class="text-sm text-secondary mb-2">Logo (SVG)</div>
            <div class="h-12 flex items-center mb-2">
              <Image
                v-if="brandProfileState.logoUrl"
                :src="brandProfileState.logoUrl"
                image-class="h-10 max-w-24"
              />
              <span
                v-else
                class="text-sm text-secondary"
              >
                No logo uploaded
              </span>
            </div>
            <Button
              label="Upload SVG"
              severity="secondary"
              outlined
              size="sm"
              icon="cloud-arrow-up"
              :is-loading="logoIsUploading"
              @click="triggerLogoUpload('svg')"
            />
          </div>
          <div class="border border-[var(--border-color)] rounded-md p-3">
            <div class="text-sm text-secondary mb-2">Logo (PNG - used in emails)</div>
            <div class="h-12 flex items-center mb-2">
              <Image
                v-if="brandProfileState.logoPngUrl"
                :src="brandProfileState.logoPngUrl"
                image-class="h-10 max-w-24"
              />
              <span
                v-else
                class="text-sm text-secondary"
              >
                No logo uploaded
              </span>
            </div>
            <Button
              label="Upload PNG"
              severity="secondary"
              outlined
              size="sm"
              icon="cloud-arrow-up"
              :is-loading="logoIsUploading"
              @click="triggerLogoUpload('png')"
            />
          </div>
        </div>
        <input
          ref="logoFileInput"
          type="file"
          :accept="logoUploadType === 'svg' ? '.svg' : '.png'"
          style="display: none"
          @change="uploadLogo"
        />
      </div>
    </Modal>
  </teleport>
</template>

<script setup>
import { ref } from 'vue'

import PageHeader from '@/components/page/PageHeader.vue'
import Card from '@/components/card/Card.vue'
import Button from '@/components/button/Button.vue'
import Input from '@/components/forms/Input.vue'
import InputError from '@/components/forms/InputError.vue'
import FormGroup from '@/components/forms/FormGroup.vue'
import Modal from '@/components/modal/Modal.vue'
import Chip from '@/components/chip/Chip.vue'
import Image from '@/components/image/Image.vue'
import Select from '@/components/forms/Select.vue'

import useApiRequest from '@/composables/useApiRequest'
import { alertToast } from '@/utilities/notification'

import { FilterMatchMode } from '@primevue/core/api'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import _toUpper from 'lodash/toUpper'
import { useI18n } from 'vue-i18n'
import useVuelidate from '@vuelidate/core'
import { required } from '@vuelidate/validators'

const manufacturers = ref(null)
const manufacturersApiRequest = useApiRequest()
const isLoading = manufacturersApiRequest.isLoading

const filters = ref([])

function initFilters() {
  filters.value = {
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    isActive: { value: null, matchMode: FilterMatchMode.IN }
  }
}
initFilters()

function clearFilters() {
  // remove from session storage
  sessionStorage.removeItem('dt-settings-manufacturers')
  initFilters()
}

const regionsList = [
  {
    label: useI18n().t('meta.countries.gb.label'),
    value: 'gb',
    emoji: '🇬🇧',
    image: 'https://cdn.jsdelivr.net/npm/country-flag-emoji-json@2.0.0/dist/images/GB.svg'
  },
  {
    label: useI18n().t('meta.countries.us.label'),
    value: 'us',
    emoji: '🇺🇸',
    image: 'https://cdn.jsdelivr.net/npm/country-flag-emoji-json@2.0.0/dist/images/US.svg'
  },
  {
    label: useI18n().t('meta.countries.es.label'),
    value: 'es',
    emoji: '🇪🇸',
    image: 'https://cdn.jsdelivr.net/npm/country-flag-emoji-json@2.0.0/dist/images/ES.svg'
  },
  {
    label: useI18n().t('meta.countries.mx.label'),
    value: 'mx',
    emoji: '🇲🇽',
    image: 'https://cdn.jsdelivr.net/npm/country-flag-emoji-json@2.0.0/dist/images/MX.svg'
  },
  {
    label: useI18n().t('meta.countries.fr.label'),
    value: 'fr',
    emoji: '🇫🇷',
    image: 'https://cdn.jsdelivr.net/npm/country-flag-emoji-json@2.0.0/dist/images/FR.svg'
  }
]

function fetchManufacturers() {
  manufacturersApiRequest
    .send({
      endpoint: '/v1/manufacturers'
    })
    .then(response => {
      if (response.success) {
        manufacturers.value = response.data
      } else {
        alertToast('Error', 'Failed to fetch manufacturers', 'error')
      }
    })
}
fetchManufacturers()

const brandProfileApiRequest = useApiRequest()
const logoUploadApiRequest = useApiRequest()
const logoIsUploading = logoUploadApiRequest.isLoading

const brandProfileModalIsOpen = ref(false)
const brandProfileState = ref({})

const logoFileInput = ref(null)
const logoUploadType = ref('svg')

const v$profile = useVuelidate({ name: { required } }, brandProfileState, { $stopPropagation: true })

function editBrandProfile(manufacturer) {
  brandProfileState.value = {
    id: manufacturer.brandProfileId,
    name: manufacturer.branding?.name,
    logoUrl: manufacturer.branding?.logoUrl,
    logoPngUrl: manufacturer.branding?.logoPngUrl,
    accentColor: manufacturer.branding?.accentColor,
    uiGradient1: manufacturer.branding?.uiGradient1,
    uiGradient2: manufacturer.branding?.uiGradient2
  }
  brandProfileModalIsOpen.value = true
}

async function submitBrandProfile() {
  const isFormCorrect = await v$profile.value.$validate()
  if (!isFormCorrect) {
    return
  }

  const dataToPost = {
    name: brandProfileState.value.name,
    accentColor: brandProfileState.value.accentColor,
    uiGradient1: brandProfileState.value.uiGradient1,
    uiGradient2: brandProfileState.value.uiGradient2
  }

  try {
    const response = await brandProfileApiRequest.send({
      method: 'PATCH',
      endpoint: `/v1/brand-profiles/${brandProfileState.value.id}`,
      data: JSON.stringify(dataToPost)
    })
    if (!response.success) {
      alertToast('Error', 'Failed to save brand profile', 'error')
      return
    }
    alertToast('Success!', 'Brand profile saved', 'success')
    brandProfileModalIsOpen.value = false
    fetchManufacturers()
  } catch (err) {
    alertToast('Error', err.message, 'error')
  }
  v$profile.value.$reset()
}

function triggerLogoUpload(type) {
  logoUploadType.value = type
  logoFileInput.value.click()
}

async function uploadLogo(event) {
  const file = event.target.files[0]
  event.target.value = ''
  // Nothing selected in the file dialog
  if (!file) {
    return
  }

  const formData = new FormData()
  formData.append('file', file)

  try {
    const response = await logoUploadApiRequest.send({
      method: 'POST',
      endpoint: `/v1/brand-profiles/${brandProfileState.value.id}/logo`,
      data: formData
    })
    if (!response.success) {
      alertToast('Error', 'Failed to upload logo', 'error')
      return
    }
    alertToast('Success!', 'Logo uploaded', 'success')
    // API returns logoUrl (svg) or logoPngUrl (png) for the uploaded file
    if (response.data.logoUrl) {
      brandProfileState.value.logoUrl = response.data.logoUrl
    }
    if (response.data.logoPngUrl) {
      brandProfileState.value.logoPngUrl = response.data.logoPngUrl
    }
    fetchManufacturers()
  } catch (err) {
    alertToast('Error', err.message, 'error')
  }
}
</script>

<style></style>
